import { lazy, Suspense, useEffect } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import * as Sentry from '@sentry/react';
import { useBootstrap } from './hooks/useBootstrap';
import { useAuthStore } from './stores/authStore';
import { ROUTES } from './constants/routes';
import { MODULES } from './constants/modules';

const SentryRoutes = import.meta.env.PROD
  ? Sentry.withSentryReactRouterV6Routing(Routes)
  : Routes;

// Layouts
import AuthLayout from './components/layout/AuthLayout';
import AppLayout from './components/layout/AppLayout';
import PlatformLayout from './components/layout/PlatformLayout';
import BrokerLayout from './components/layout/BrokerLayout';

// Guards
import AuthGuard from './guards/AuthGuard';
import ModuleGuard from './guards/ModuleGuard';
import PermissionRouteGuard from './guards/PermissionRouteGuard';
import RoleRouteGuard from './guards/RoleRouteGuard';
import MustChangePasswordGuard from './guards/MustChangePasswordGuard';

// Skeleton
import PageSkeleton from './components/common/PageSkeleton';

// Auth pages (eager — needed immediately)
import LoginPage from './pages/auth/LoginPage';
import MfaVerifyPage from './pages/auth/MfaVerifyPage';

// Landing page — public marketing page
const LandingPage = lazy(() => import('./pages/landing/LandingPage'));
const TermsPage = lazy(() => import('./pages/landing/TermsPage'));
const PrivacyPage = lazy(() => import('./pages/landing/PrivacyPage'));

// Lazy pages — first-login password change
const FirstLoginChangePasswordPage = lazy(() => import('./pages/auth/FirstLoginChangePasswordPage'));

// Lazy pages — forgot password
const ForgotPasswordPage = lazy(() => import('./pages/auth/ForgotPasswordPage'));

// Lazy pages — onboarding
const OnboardingPage = lazy(() => import('./pages/onboarding/OnboardingPage'));
const OnboardingCompletePage = lazy(() => import('./pages/onboarding/OnboardingCompletePage'));

// Lazy pages — dashboard
const DashboardHome = lazy(() => import('./pages/dashboard/DashboardHome'));
const GuidePage = lazy(() => import('./pages/guide/GuidePage'));
const SupportPage = lazy(() => import('./pages/support/SupportPage'));

// Lazy pages — policies
const PoliciesListPage = lazy(() => import('./pages/policies/PoliciesListPage'));
const PolicyNewPage = lazy(() => import('./pages/policies/PolicyNewPage'));
const PolicyDetailPage = lazy(() => import('./pages/policies/PolicyDetailPage'));
const PolicyRenewalsPage = lazy(() => import('./pages/policies/PolicyRenewalsPage'));
const PolicyEndorsePage = lazy(() => import('./pages/policies/PolicyEndorsePage'));
const UnderwritingQueuePage = lazy(() => import('./pages/policies/UnderwritingQueuePage'));

// Lazy pages — products
const ProductsListPage = lazy(() => import('./pages/products/ProductsListPage'));
const ProductNewPage = lazy(() => import('./pages/products/ProductNewPage'));
const ProductDetailPage = lazy(() => import('./pages/products/ProductDetailPage'));

// Lazy pages — claims
const ClaimsListPage = lazy(() => import('./pages/claims/ClaimsListPage'));
const ClaimNewPage = lazy(() => import('./pages/claims/ClaimNewPage'));
const ClaimDetailPage = lazy(() => import('./pages/claims/ClaimDetailPage'));
const RecourseListPage = lazy(() => import('./pages/claims/RecourseListPage'));
const RecourseDetailPage = lazy(() => import('./pages/claims/RecourseDetailPage'));
const LitigationListPage = lazy(() => import('./pages/claims/LitigationListPage'));
const LitigationDetailPage = lazy(() => import('./pages/claims/LitigationDetailPage'));
const AppChannelClaimDetailPage = lazy(() => import('./pages/claims/AppChannelClaimDetailPage'));

// Lazy pages — clients & agents
const ClientsListPage = lazy(() => import('./pages/clients/ClientsListPage'));
const ClientNewPage = lazy(() => import('./pages/clients/ClientNewPage'));
const ClientDetailPage = lazy(() => import('./pages/clients/ClientDetailPage'));
const AgentsListPage = lazy(() => import('./pages/agents/AgentsListPage'));
const AgentNewPage = lazy(() => import('./pages/agents/AgentNewPage'));
const AgentDetailPage = lazy(() => import('./pages/agents/AgentDetailPage'));

// Lazy pages — billing
const BillingOverviewPage = lazy(() => import('./pages/billing/BillingOverviewPage'));
const FinancePage = lazy(() => import('./pages/billing/FinancePage'));
const CommissionsPage = lazy(() => import('./pages/billing/CommissionsPage'));
const ReinsurancePage = lazy(() => import('./pages/billing/ReinsurancePage'));
const TaxPage = lazy(() => import('./pages/billing/TaxPage'));
const ReservesPage = lazy(() => import('./pages/billing/ReservesPage'));
const SolvencyPage = lazy(() => import('./pages/billing/SolvencyPage'));
const PeriodsPage = lazy(() => import('./pages/billing/PeriodsPage'));
const IntermediaryRemittancePage = lazy(() => import('./pages/billing/IntermediaryRemittancePage'));
const RevenueAccountPage = lazy(() => import('./pages/billing/RevenueAccountPage'));

// Lazy pages — analytics
const AnalyticsOverviewPage = lazy(() => import('./pages/analytics/AnalyticsOverviewPage'));
const UnderwritingReportPage = lazy(() => import('./pages/analytics/UnderwritingReportPage'));
const ClaimsReportPage = lazy(() => import('./pages/analytics/ClaimsReportPage'));
const FinancialReportPage = lazy(() => import('./pages/analytics/FinancialReportPage'));
const AgentPerformancePage = lazy(() => import('./pages/analytics/AgentPerformancePage'));
const CapitalAdequacyPage = lazy(() => import('./pages/analytics/CapitalAdequacyPage'));
const CapitalAdequacyInputPage = lazy(() => import('./pages/analytics/CapitalAdequacyInputPage'));

// Lazy pages — HR
const HrOverviewPage = lazy(() => import('./pages/hr/HrOverviewPage'));
const EmployeesListPage = lazy(() => import('./pages/hr/EmployeesListPage'));
const EmployeeNewPage = lazy(() => import('./pages/hr/EmployeeNewPage'));
const EmployeeDetailPage = lazy(() => import('./pages/hr/EmployeeDetailPage'));
const LeaveRequestsPage = lazy(() => import('./pages/hr/LeaveRequestsPage'));
const HrOrgChartPage = lazy(() => import('./pages/hr/HrOrgChartPage'));

// Lazy pages — STP
const StpRulesPage = lazy(() => import('./pages/stp/StpRulesPage'));
const StpAnalyticsPage = lazy(() => import('./pages/stp/StpAnalyticsPage'));

// Lazy pages — AI Insurance Intelligence
const CopilotPage = lazy(() => import('./pages/copilot/CopilotPage'));

// Lazy pages — Fast Inspections
const InspectionsPage = lazy(() => import('./pages/inspections/InspectionsPage'));
const InspectionDetailPage = lazy(() => import('./pages/inspections/InspectionDetailPage'));

// Lazy pages — settings
const SettingsPage = lazy(() => import('./pages/settings/SettingsPage'));
const UsersPage = lazy(() => import('./pages/settings/UsersPage'));
const BillingSettingsPage = lazy(() => import('./pages/settings/BillingSettingsPage'));
// const RolesPage = lazy(() => import('./pages/settings/RolesPage')); // POST-LAUNCH
const WebhooksPage = lazy(() => import('./pages/settings/WebhooksPage'));
const ApiKeysPage = lazy(() => import('./pages/settings/ApiKeysPage'));
const MfaPage = lazy(() => import('./pages/settings/MfaPage'));
const ProfilePage = lazy(() => import('./pages/settings/ProfilePage'));
const AuditLogPage = lazy(() => import('./pages/settings/AuditLogPage'));
const MigrationPage = lazy(() => import('./pages/settings/MigrationPage'));
const CertificatesPage = lazy(() => import('./pages/settings/CertificatesPage'));
const WordingPage = lazy(() => import('./pages/settings/WordingPage'));

// Lazy pages — subscription
const UpgradePage = lazy(() => import('./pages/subscription/UpgradePage'));
const SubscriptionLockedPage = lazy(() => import('./pages/subscription/SubscriptionLockedPage'));
const SubscriptionConfirmPage = lazy(() => import('./pages/subscription/SubscriptionConfirmPage'));
const PaymentSuccessPage = lazy(() => import('./pages/subscription/PaymentSuccessPage'));

// Lazy pages — platform
const PlatformDashboard = lazy(() => import('./pages/platform/PlatformDashboard'));
const PlatformClientsPage = lazy(() => import('./pages/platform/PlatformClientsPage'));
const PlatformClientNewPage = lazy(() => import('./pages/platform/PlatformClientNewPage'));
const PlatformClientDetailPage = lazy(() => import('./pages/platform/PlatformClientDetailPage'));
const PlatformClientEditPage = lazy(() => import('./pages/platform/PlatformClientEditPage'));
const PlatformSubscriptionsPage = lazy(() => import('./pages/platform/PlatformSubscriptionsPage'));
const PlatformTrialsPage = lazy(() => import('./pages/platform/PlatformTrialsPage'));
const PlatformBillingPage = lazy(() => import('./pages/platform/PlatformBillingPage'));
const PlatformInvoicesPage = lazy(() => import('./pages/platform/PlatformInvoicesPage'));
const PlatformRevenuePage = lazy(() => import('./pages/platform/PlatformRevenuePage'));
const PlatformModulesPage = lazy(() => import('./pages/platform/PlatformModulesPage'));
const PlatformAuditPage = lazy(() => import('./pages/platform/PlatformAuditPage'));
const PlatformActivityPage = lazy(() => import('./pages/platform/PlatformActivityPage'));
const PlatformSettingsPage = lazy(() => import('./pages/platform/PlatformSettingsPage'));

// Lazy pages — broker portal
const BrokerDashboard = lazy(() => import('./pages/broker/BrokerDashboard'));
const BrokerPolicies = lazy(() => import('./pages/broker/BrokerPolicies'));
const BrokerPolicyDetail = lazy(() => import('./pages/broker/BrokerPolicyDetail'));
const BrokerCommissions = lazy(() => import('./pages/broker/BrokerCommissions'));
const BrokerDocuments = lazy(() => import('./pages/broker/BrokerDocuments'));
const BrokerMessages = lazy(() => import('./pages/broker/BrokerMessages'));
const BrokerNewMessage = lazy(() => import('./pages/broker/BrokerNewMessage'));
const BrokerMessageDetail = lazy(() => import('./pages/broker/BrokerMessageDetail'));

// Lazy pages — misc
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));
const ChangePasswordPage = lazy(() => import('./pages/settings/ChangePasswordPage'));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60 * 1000,
      gcTime: 10 * 60 * 1000,
    },
  },
});

function AppBootstrapLoader() {
  return (
    <div className="fixed inset-0 flex flex-col items-center justify-center bg-white">
      <p className="text-lg font-bold text-gray-900 mb-6">Fast Policy Enterprise</p>
      <div className="h-10 w-10 rounded-full border-4 border-gray-200 border-t-primary animate-spin" />
    </div>
  );
}

function LandingOrRedirect() {
  const token = useAuthStore((s) => s.token);
  const user = useAuthStore((s) => s.user);
  const isSuperAdmin = useAuthStore((s) => s.isSuperAdmin);
  const isIcBroker = useAuthStore((s) => s.isIcBroker);

  if (token && user) {
    if (isSuperAdmin()) return <Navigate to={ROUTES.PLATFORM} replace />;
    if (isIcBroker()) return <Navigate to={ROUTES.BROKER_DASHBOARD} replace />;
    return <Navigate to={ROUTES.DASHBOARD} replace />;
  }

  return <LandingPage />;
}

function AppInner() {
  const { isReady } = useBootstrap();
  const user = useAuthStore((s) => s.user);
  const company = useAuthStore((s) => s.company);

  useEffect(() => {
    if (!import.meta.env.PROD) return;
    if (user) {
      Sentry.setUser({ id: user.id, username: user.full_name, company_id: company?.id });
    } else {
      Sentry.setUser(null);
    }
  }, [user, company]);

  if (!isReady) {
    return <AppBootstrapLoader />;
  }

  return (
    <Suspense fallback={<PageSkeleton />}>
      <SentryRoutes>
          <Route path={ROUTES.HOME} element={<LandingOrRedirect />} />
          <Route path={ROUTES.TERMS} element={<TermsPage />} />
          <Route path={ROUTES.PRIVACY} element={<PrivacyPage />} />

          {/* Auth — no guard */}
          <Route element={<AuthLayout />}>
            <Route path={ROUTES.LOGIN} element={<LoginPage />} />
            <Route path={ROUTES.MFA} element={<MfaVerifyPage />} />
            <Route path={ROUTES.FORGOT_PASSWORD} element={<ForgotPasswordPage />} />
          </Route>

          {/* Onboarding — standalone full-page layout, requires auth */}
          <Route element={<AuthGuard />}>
            <Route path={ROUTES.ONBOARDING} element={<OnboardingPage />} />
            <Route path={ROUTES.ONBOARDING_COMPLETE} element={<OnboardingCompletePage />} />
          </Route>

          {/* First-login password change — only accessible when must_change_password=true */}
          <Route
            path={ROUTES.FIRST_LOGIN_CHANGE_PASSWORD}
            element={
              <MustChangePasswordGuard>
                <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 p-4">
                  <div className="mb-8 flex flex-col items-center gap-2">
                    <span className="text-2xl font-bold text-gray-900">Fast Policy Enterprise</span>
                    <span className="text-sm text-gray-500">Powered by Fastclaim</span>
                  </div>
                  <FirstLoginChangePasswordPage />
                </div>
              </MustChangePasswordGuard>
            }
          />

          {/* Upgrade / locked / confirm / payment-success — AuthGuard only, NOT inside ModuleGuard (prevents infinite redirect) */}
          <Route element={<AuthGuard />}>
            <Route path={ROUTES.UPGRADE} element={<UpgradePage />} />
            <Route path={ROUTES.SUBSCRIPTION_LOCKED} element={<SubscriptionLockedPage />} />
            <Route path={ROUTES.SUBSCRIPTION_CONFIRM} element={<SubscriptionConfirmPage />} />
            <Route path={ROUTES.PAYMENT_SUCCESS} element={<PaymentSuccessPage />} />
          </Route>

          {/* Change password — AuthGuard only, bypasses ModuleGuard */}
          <Route element={<AuthGuard />}>
            <Route path="/dashboard/change-password" element={<ChangePasswordPage />} />
          </Route>

          {/* Dashboard — AuthGuard + outer ModuleGuard (isLocked check) + AppLayout */}
          <Route element={<AuthGuard />}>
            <Route element={<ModuleGuard />}>
              <Route element={<AppLayout />}>
                <Route path={ROUTES.DASHBOARD} element={<DashboardHome />} />
                <Route path={ROUTES.SUBSCRIPTION} element={<Navigate to={ROUTES.SETTINGS_BILLING} replace />} />

                {/* Policy Admin */}
                <Route element={<ModuleGuard moduleId={MODULES.POLICY_ADMIN} />}>
                  {/* Policy read — all staff with module access */}
                  <Route path={ROUTES.POLICIES} element={<PoliciesListPage />} />
                  <Route path={ROUTES.POLICY_DETAIL} element={<PolicyDetailPage />} />
                  {/* Policy write — policy:create */}
                  <Route element={<PermissionRouteGuard permission="policy:create" />}>
                    <Route path={ROUTES.POLICY_NEW} element={<PolicyNewPage />} />
                  </Route>
                  {/* Policy renewals — policy:renew */}
                  <Route element={<PermissionRouteGuard permission="policy:renew" />}>
                    <Route path={ROUTES.POLICY_RENEWALS} element={<PolicyRenewalsPage />} />
                  </Route>
                  {/* Policy endorse — policy:endorse */}
                  <Route element={<PermissionRouteGuard permission="policy:endorse" />}>
                    <Route path={ROUTES.POLICY_ENDORSE} element={<PolicyEndorsePage />} />
                  </Route>
                  {/* UW Queue — policy:read */}
                  <Route element={<PermissionRouteGuard permission="policy:read" />}>
                    <Route path="/dashboard/policies/queue" element={<UnderwritingQueuePage />} />
                  </Route>
                  {/* Products — product:read */}
                  <Route element={<PermissionRouteGuard permission="product:read" />}>
                    <Route path={ROUTES.PRODUCTS} element={<ProductsListPage />} />
                    <Route path={ROUTES.PRODUCT_NEW} element={<ProductNewPage />} />
                    <Route path={ROUTES.PRODUCT_DETAIL} element={<ProductDetailPage />} />
                    <Route path={ROUTES.PRODUCT_EDIT} element={<ProductNewPage />} />
                  </Route>
                </Route>

                {/* Claims */}
                <Route element={<ModuleGuard moduleId={MODULES.CLAIMS} />}>
                  {/* Claims read — requires claim:read */}
                  <Route element={<PermissionRouteGuard permission="claim:read" />}>
                    <Route path={ROUTES.CLAIMS} element={<ClaimsListPage />} />
                    <Route path={ROUTES.APP_CHANNEL_CLAIM_DETAIL} element={<AppChannelClaimDetailPage />} />
                    <Route path={ROUTES.CLAIM_DETAIL} element={<ClaimDetailPage />} />
                    <Route path={ROUTES.RECOURSE_LIST} element={<RecourseListPage />} />
                    <Route path={ROUTES.RECOURSE_DETAIL} element={<RecourseDetailPage />} />
                    <Route path={ROUTES.LITIGATION_LIST} element={<LitigationListPage />} />
                    <Route path={ROUTES.LITIGATION_DETAIL} element={<LitigationDetailPage />} />
                    {/* Claims write — requires claim:assign */}
                    <Route element={<PermissionRouteGuard permission="claim:assign" />}>
                      <Route path={ROUTES.CLAIM_NEW} element={<ClaimNewPage />} />
                    </Route>
                  </Route>
                </Route>

                {/* Clients & Agents */}
                <Route element={<ModuleGuard moduleId={MODULES.CLIENT_AGENT} />}>
                  <Route path={ROUTES.CLIENTS} element={<ClientsListPage />} />
                  <Route path={ROUTES.CLIENT_DETAIL} element={<ClientDetailPage />} />
                  {/* Client create — client:create */}
                  <Route element={<PermissionRouteGuard permission="client:create" />}>
                    <Route path={ROUTES.CLIENT_NEW} element={<ClientNewPage />} />
                  </Route>
                  <Route path={ROUTES.AGENTS} element={<AgentsListPage />} />
                  <Route path={ROUTES.AGENT_DETAIL} element={<AgentDetailPage />} />
                  {/* Agent create — agent:create */}
                  <Route element={<PermissionRouteGuard permission="agent:create" />}>
                    <Route path={ROUTES.AGENT_NEW} element={<AgentNewPage />} />
                  </Route>
                </Route>

                {/* Billing & Finance */}
                <Route element={<ModuleGuard moduleId="billing_finance" />}>
                  <Route element={<PermissionRouteGuard permission="finance:read" />}>
                    <Route path={ROUTES.BILLING} element={<BillingOverviewPage />} />
                    <Route path={ROUTES.BILLING_COMMISSIONS} element={<CommissionsPage />} />
                    <Route path={ROUTES.BILLING_REINSURANCE} element={<ReinsurancePage />} />
                    <Route path={ROUTES.BILLING_TAX} element={<TaxPage />} />
                    {/* Finance: merged Premiums + Ledger + Overdue */}
                    <Route path="/dashboard/billing/finance">
                      <Route index element={<Navigate to={ROUTES.BILLING_FINANCE_PREMIUMS} replace />} />
                      <Route path="premiums" element={<FinancePage />} />
                      <Route path="ledger" element={<FinancePage />} />
                      <Route path="overdue" element={<FinancePage />} />
                    </Route>
                    {/* Redirects from old routes */}
                    <Route path={ROUTES.BILLING_PREMIUMS} element={<Navigate to={ROUTES.BILLING_FINANCE_PREMIUMS} replace />} />
                    <Route path={ROUTES.BILLING_LEDGER} element={<Navigate to={ROUTES.BILLING_FINANCE_LEDGER} replace />} />
                    {/* New billing pages */}
                    <Route path={ROUTES.BILLING_RESERVES} element={<ReservesPage />} />
                    <Route path={ROUTES.BILLING_SOLVENCY} element={<SolvencyPage />} />
                    <Route path={ROUTES.BILLING_PERIODS} element={<PeriodsPage />} />
                    <Route path={ROUTES.BILLING_REMITTANCES} element={<IntermediaryRemittancePage />} />
                    <Route path={ROUTES.BILLING_REVENUE_ACCOUNT} element={<RevenueAccountPage />} />
                  </Route>
                </Route>

                {/* Analytics */}
                <Route element={<ModuleGuard moduleId="analytics" />}>
                  <Route element={<PermissionRouteGuard permission="analytics:read" />}>
                    <Route path={ROUTES.ANALYTICS} element={<AnalyticsOverviewPage />} />
                    <Route path={ROUTES.ANALYTICS_UNDERWRITING} element={<UnderwritingReportPage />} />
                    <Route path={ROUTES.ANALYTICS_CLAIMS} element={<ClaimsReportPage />} />
                    <Route path={ROUTES.ANALYTICS_FINANCIAL} element={<FinancialReportPage />} />
                    <Route path={ROUTES.ANALYTICS_AGENTS} element={<AgentPerformancePage />} />
                    <Route path={ROUTES.ANALYTICS_STP} element={<StpAnalyticsPage />} />
                  </Route>
                  <Route element={<PermissionRouteGuard permission="capital_adequacy:read" />}>
                    <Route path={ROUTES.ANALYTICS_CAPITAL_ADEQUACY} element={<CapitalAdequacyPage />} />
                  </Route>
                  <Route element={<PermissionRouteGuard permission="capital_adequacy:input" />}>
                    <Route path={ROUTES.ANALYTICS_CAPITAL_ADEQUACY_INPUT} element={<CapitalAdequacyInputPage />} />
                  </Route>
                </Route>

                {/* AI Insurance Intelligence */}
                <Route element={<ModuleGuard moduleId={MODULES.AI_COPILOT} />}>
                  <Route path={ROUTES.COPILOT} element={<CopilotPage />} />
                </Route>

                {/* Fast Inspections */}
                <Route element={<ModuleGuard moduleId={MODULES.FAST_INSPECTIONS} />}>
                  <Route path={ROUTES.INSPECTIONS} element={<InspectionsPage />} />
                  <Route path={ROUTES.INSPECTION_DETAIL} element={<InspectionDetailPage />} />
                </Route>

                {/* HR — requires hr:read */}
                <Route element={<ModuleGuard moduleId={MODULES.HR} />}>
                  <Route element={<PermissionRouteGuard permission="hr:read" />}>
                    <Route path={ROUTES.HR} element={<HrOverviewPage />} />
                    <Route path={ROUTES.HR_EMPLOYEES} element={<EmployeesListPage />} />
                    <Route path={ROUTES.HR_EMPLOYEE} element={<EmployeeDetailPage />} />
                    <Route path={ROUTES.HR_LEAVE} element={<LeaveRequestsPage />} />
                    <Route path={ROUTES.HR_ORG_CHART} element={<HrOrgChartPage />} />
                  </Route>
                  <Route element={<PermissionRouteGuard permission="hr:write" />}>
                    <Route path={ROUTES.HR_EMPLOYEE_NEW} element={<EmployeeNewPage />} />
                  </Route>
                </Route>

                {/* STP — requires stp:read */}
                <Route element={<ModuleGuard moduleId="stp" />}>
                  <Route element={<PermissionRouteGuard permission="stp:read" />}>
                    <Route path={ROUTES.STP_RULES} element={<StpRulesPage />} />
                    {/* Redirect old STP analytics URL to new location under analytics module */}
                    <Route path={ROUTES.STP_ANALYTICS} element={<Navigate to={ROUTES.ANALYTICS_STP} replace />} />
                  </Route>
                </Route>

                {/* Settings — ic-admin only (settings:company) */}
                <Route element={<PermissionRouteGuard permission="settings:company" />}>
                  <Route path="/dashboard/settings" element={<Navigate to={ROUTES.SETTINGS_GENERAL} replace />} />
                  <Route path={ROUTES.SETTINGS_GENERAL}         element={<SettingsPage />} />
                  <Route path={ROUTES.SETTINGS_COMPANY_PROFILE} element={<SettingsPage />} />
                  <Route path={ROUTES.SETTINGS_BRANDING}        element={<SettingsPage />} />
                  <Route path={ROUTES.SETTINGS_FRAUD_RULES}     element={<SettingsPage />} />
                  <Route path={ROUTES.SETTINGS_BLACKLIST}       element={<SettingsPage />} />
                  <Route path={ROUTES.SETTINGS_TAX}             element={<SettingsPage />} />
                  <Route path={ROUTES.SETTINGS_USERS} element={<UsersPage />} />
                  {/* POST-LAUNCH: <Route path={ROUTES.SETTINGS_ROLES} element={<RolesPage />} /> */}
                  <Route element={<ModuleGuard moduleId={MODULES.INTEGRATION} />}>
                    <Route path={ROUTES.SETTINGS_WEBHOOKS} element={<WebhooksPage />} />
                    <Route path={ROUTES.SETTINGS_API_KEYS} element={<ApiKeysPage />} />
                    <Route path={ROUTES.SETTINGS_MIGRATION} element={<MigrationPage />} />
                  </Route>
                  <Route path={ROUTES.SETTINGS_CERTIFICATES} element={<CertificatesPage />} />
                  <Route path={ROUTES.SETTINGS_WORDING} element={<WordingPage />} />
                  <Route path={ROUTES.SETTINGS_BILLING} element={<BillingSettingsPage />} />
                </Route>
                {/* Audit Log — ic-admin + ic-auditor */}
                <Route element={<RoleRouteGuard roles={['ic-admin', 'ic-auditor']} />}>
                  <Route path={ROUTES.SETTINGS_AUDIT_LOG} element={<AuditLogPage />} />
                </Route>
                {/* Settings accessible to all authenticated staff */}
                <Route path={ROUTES.SETTINGS_PROFILE} element={<ProfilePage />} />
                <Route path={ROUTES.SETTINGS_MFA} element={<MfaPage />} />
                {/* User Guide — accessible to all authenticated staff */}
                <Route path={ROUTES.GUIDE} element={<GuidePage />} />
                {/* Help & Support — accessible to all authenticated staff */}
                <Route path={ROUTES.SUPPORT} element={<SupportPage />} />
              </Route>{/* AppLayout */}
            </Route>{/* outer ModuleGuard */}
          </Route>{/* AuthGuard */}

          {/* Broker Portal — AuthGuard + BrokerLayout (broker role check inside) */}
          <Route element={<AuthGuard />}>
            <Route element={<BrokerLayout />}>
              <Route path={ROUTES.BROKER_DASHBOARD} element={<BrokerDashboard />} />
              <Route path={ROUTES.BROKER_POLICIES} element={<BrokerPolicies />} />
              <Route path={ROUTES.BROKER_POLICY_DETAIL} element={<BrokerPolicyDetail />} />
              <Route path={ROUTES.BROKER_COMMISSIONS} element={<BrokerCommissions />} />
              <Route path={ROUTES.BROKER_DOCUMENTS} element={<BrokerDocuments />} />
              <Route path={ROUTES.BROKER_NEW_MESSAGE} element={<BrokerNewMessage />} />
              <Route path={ROUTES.BROKER_MESSAGE_DETAIL} element={<BrokerMessageDetail />} />
              <Route path={ROUTES.BROKER_MESSAGES} element={<BrokerMessages />} />
            </Route>
          </Route>

          {/* Platform — AuthGuard + PlatformLayout (super-admin check inside) */}
          <Route element={<AuthGuard />}>
            <Route element={<PlatformLayout />}>
              <Route path={ROUTES.PLATFORM} element={<PlatformDashboard />} />
              <Route path={ROUTES.PLATFORM_CLIENTS} element={<PlatformClientsPage />} />
              <Route path={ROUTES.PLATFORM_CLIENT_NEW} element={<PlatformClientNewPage />} />
              <Route path={ROUTES.PLATFORM_CLIENT_DETAIL} element={<PlatformClientDetailPage />} />
              <Route path={ROUTES.PLATFORM_CLIENT_EDIT} element={<PlatformClientEditPage />} />
              <Route path={ROUTES.PLATFORM_SUBSCRIPTIONS} element={<PlatformSubscriptionsPage />} />
              <Route path={ROUTES.PLATFORM_TRIALS} element={<PlatformTrialsPage />} />
              <Route path={ROUTES.PLATFORM_BILLING} element={<PlatformBillingPage />} />
              <Route path={ROUTES.PLATFORM_INVOICES} element={<PlatformInvoicesPage />} />
              <Route path={ROUTES.PLATFORM_REVENUE} element={<PlatformRevenuePage />} />
              <Route path={ROUTES.PLATFORM_MODULES} element={<PlatformModulesPage />} />
              <Route path={ROUTES.PLATFORM_ACTIVITY} element={<PlatformActivityPage />} />
              <Route path={ROUTES.PLATFORM_AUDIT} element={<PlatformActivityPage />} />
              <Route path={ROUTES.PLATFORM_SETTINGS} element={<PlatformSettingsPage />} />
            </Route>
          </Route>

          <Route path="*" element={<NotFoundPage />} />
        </SentryRoutes>
      </Suspense>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AppInner />
        <Toaster position="top-right" />
      </BrowserRouter>
      <ReactQueryDevtools initialIsOpen={false} />
    </QueryClientProvider>
  );
}

export default App;
